import React, { useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { isRTL } from '@/core/i18n'
import { Search, FileDown, Copy, ChevronLeft, ChevronRight, Download, Eye } from 'lucide-react'
import toast from 'react-hot-toast'

import { PageHeader } from '@/components/shared/PageHeader'
import { EmptyState } from '@/components/shared/EmptyState'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { useAuthStore } from '@/store/auth.store'
import { reportService, VatReportEntry } from '@/services/reportService'

export const VatReturnReport: React.FC = () => {
  const { t, i18n } = useTranslation('common')
  const rtl = isRTL(i18n.language)
  const { user } = useAuthStore()

  const [vatData, setVatData] = useState<VatReportEntry[]>([])
  const [loading, setLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  // Pagination states
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(100)
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(0)

  // Filter states
  const [fromDate, setFromDate] = useState<string>('')
  const [toDate, setToDate] = useState<string>('')

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>({
    id: true,
    date: true,
    vendorCustomer: true,
    type: true,
    number: true,
    reference: true,
    note: true,
    taxableAmount: true,
    vatDebit: true,
    vatCredit: true,
    balance: true,
  })

  // Get branch ID from user's selected branch
  const selectedBranchId = user?.selectedBranch?.branchId

  // Set default dates (current month)
  useEffect(() => {
    const today = new Date()
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1)
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0)
    
    setFromDate(formatDateForInput(firstDay))
    setToDate(formatDateForInput(lastDay))
  }, [])

  const formatDateForInput = (date: Date): string => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-'
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    } catch {
      return dateString
    }
  }

  const formatAmount = (amount?: number) => {
    if (amount === undefined || amount === null) return '0.00'
    return Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  const handleSearch = async (resetPage: boolean = false) => {
    if (!selectedBranchId) {
      toast.error('Please select a branch')
      return
    }

    if (!fromDate || !toDate) {
      toast.error('Please select both from date and to date')
      return
    }

    try {
      setLoading(true)
      // Convert dates to ISO date-time format (YYYY-MM-DDTHH:mm:ss)
      const fromDateTime = `${fromDate}T00:00:00`
      const toDateTime = `${toDate}T23:59:59`
      
      const currentPage = resetPage ? 1 : pageNumber
      if (resetPage) {
        setPageNumber(1)
      }
      
      const response = await reportService.getVatReport({
        fromDate: fromDateTime,
        toDate: toDateTime,
        branchId: selectedBranchId,
        pageNumber: currentPage,
        pageSize: pageSize,
      })
      setVatData(response.data || [])
      setTotalCount(response.totalRecords || 0)
      const pages = response.totalRecords ? Math.ceil(response.totalRecords / pageSize) : 0
      setTotalPages(pages)
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to load VAT return report')
      setVatData([])
      setTotalCount(0)
      setTotalPages(0)
    } finally {
      setLoading(false)
    }
  }

  const filteredData = useMemo(() => {
    if (!searchQuery.trim()) return vatData
    const query = searchQuery.toLowerCase()
    return vatData.filter((entry) => {
      return (
        (entry.vendorCustomer || '').toLowerCase().includes(query) ||
        (entry.type || '').toLowerCase().includes(query) ||
        (entry.number || '').toLowerCase().includes(query) ||
        (entry.reference || '').toLowerCase().includes(query) ||
        (entry.note || '').toLowerCase().includes(query) ||
        entry.id?.toString().includes(query)
      )
    })
  }, [vatData, searchQuery])

  // Calculate totals
  const totals = useMemo(() => {
    return filteredData.reduce(
      (acc, entry) => {
        acc.taxableAmount += entry.taxableAmount || 0
        acc.vatDebit += entry.vatDebit || 0
        acc.vatCredit += entry.vatCredit || 0
        return acc
      },
      { taxableAmount: 0, vatDebit: 0, vatCredit: 0 }
    )
  }, [filteredData])

  // Handle page size change
  const handlePageSizeChange = (newSize: string) => {
    setPageSize(Number(newSize))
    setPageNumber(1)
    // Trigger search with new page size
    if (selectedBranchId && fromDate && toDate) {
      setTimeout(() => handleSearch(true), 0)
    }
  }

  // Handle page change
  useEffect(() => {
    if (selectedBranchId && fromDate && toDate && pageNumber > 1 && vatData.length > 0) {
      handleSearch(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber])

  const handleDownload = async () => {
    if (!selectedBranchId || !fromDate || !toDate) {
      toast.error('Please select date range before downloading')
      return
    }

    try {
      setLoading(true)
      // Convert dates to ISO date-time format
      const fromDateTime = `${fromDate}T00:00:00`
      const toDateTime = `${toDate}T23:59:59`
      
      const blob = await reportService.downloadVatReportExcel({
        fromDate: fromDateTime,
        toDate: toDateTime,
        branchId: selectedBranchId,
      })
      
      // Create download link
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      const dateStr = fromDate && toDate ? `${fromDate}_${toDate}` : 'report'
      link.download = `vat-return-report-${dateStr}.xlsx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      
      toast.success('Excel file downloaded successfully')
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to download Excel file')
    } finally {
      setLoading(false)
    }
  }

  const handleCopy = () => {
    if (filteredData.length === 0) {
      toast.error('No data to copy')
      return
    }

    // Create CSV content
    const headers = [
      'Id',
      'Date',
      'Vendor/Customer',
      'Type',
      'Number',
      'Reference',
      'Note',
      'Taxable Amount',
      'VAT Debit',
      'Vat Credit',
      'Balance',
    ].join('\t')

    const rows = filteredData.map(entry => [
      entry.id || '',
      formatDate(entry.date),
      entry.vendorCustomer || '',
      entry.type || '',
      entry.number || '',
      entry.reference || '',
      entry.note || '',
      formatAmount(entry.taxableAmount),
      formatAmount(entry.vatDebit),
      formatAmount(entry.vatCredit),
      formatAmount(entry.balance),
    ].join('\t'))

    const csvContent = [headers, ...rows].join('\n')
    navigator.clipboard.writeText(csvContent).then(() => {
      toast.success('Data copied to clipboard')
    }).catch(() => toast.error('Failed to copy data'))
  }

  const handleCsv = () => {
    if (filteredData.length === 0) {
      toast.error('No data to export')
      return
    }

    // Create CSV content
    const headers = [
      'Id',
      'Date',
      'Vendor/Customer',
      'Type',
      'Number',
      'Reference',
      'Note',
      'Taxable Amount',
      'VAT Debit',
      'Vat Credit',
      'Balance',
    ].join(',')

    const rows = filteredData.map(entry => [
      entry.id || '',
      `"${formatDate(entry.date)}"`,
      `"${entry.vendorCustomer || ''}"`,
      `"${entry.type || ''}"`,
      `"${entry.number || ''}"`,
      `"${entry.reference || ''}"`,
      `"${entry.note || ''}"`,
      formatAmount(entry.taxableAmount),
      formatAmount(entry.vatDebit),
      formatAmount(entry.vatCredit),
      formatAmount(entry.balance),
    ].join(','))

    // Add total row
    const totalRow = [
      '"Total"',
      '""',
      '""',
      '""',
      '""',
      '""',
      '""',
      formatAmount(totals.taxableAmount),
      formatAmount(totals.vatDebit),
      formatAmount(totals.vatCredit),
      formatAmount(totals.vatDebit - totals.vatCredit),
    ].join(',')

    const csvContent = [headers, ...rows, totalRow].join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    const dateStr = fromDate && toDate ? `${fromDate}_${toDate}` : 'report'
    link.setAttribute('download', `vat-return-${dateStr}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    toast.success('CSV file downloaded')
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={t('navigation.vatReturnReport')}
        description={t('reports.viewVatReturnReport')}
      />

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('reports.fromDate')}</Label>
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('reports.toDate')}</Label>
              <Input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>
          </div>
          <div className="mt-4 flex flex-col sm:flex-row gap-4 items-end">
            <div className="flex-1 w-full">
              <Label>{t('reports.search')}</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder={t('reports.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => handleSearch(true)} disabled={loading}>
                {loading ? t('reports.loading') : t('reports.search')}
              </Button>
              <Button variant="outline" onClick={handleDownload} disabled={loading}>
                <Download className="h-4 w-4 mr-2" />
                {t('reports.download')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="mt-4 text-muted-foreground">Loading report...</p>
            </div>
          ) : filteredData.length === 0 ? (
            <div className="p-12">
              <EmptyState
                title="No data found"
                description="Click Search to load VAT return report"
              />
            </div>
          ) : (
            <>
              {/* Table Controls */}
              <div className="border-b px-4 sm:px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex-1 w-full">
                  <Input
                    placeholder={t('reports.searchPlaceholder')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="max-w-sm"
                  />
                </div>
                <div className="flex gap-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm">
                        <Eye className="h-4 w-4 mr-2" />
                        Column Visibility
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuCheckboxItem
                        checked={visibleColumns.id}
                        onCheckedChange={(checked) => setVisibleColumns({ ...visibleColumns, id: checked })}
                      >
                        {t('reports.id')}
                      </DropdownMenuCheckboxItem>
                      <DropdownMenuCheckboxItem
                        checked={visibleColumns.date}
                        onCheckedChange={(checked) => setVisibleColumns({ ...visibleColumns, date: checked })}
                      >
                        {t('reports.date')}
                      </DropdownMenuCheckboxItem>
                      <DropdownMenuCheckboxItem
                        checked={visibleColumns.vendorCustomer}
                        onCheckedChange={(checked) => setVisibleColumns({ ...visibleColumns, vendorCustomer: checked })}
                      >
                        {t('reports.vendorCustomer')}
                      </DropdownMenuCheckboxItem>
                      <DropdownMenuCheckboxItem
                        checked={visibleColumns.type}
                        onCheckedChange={(checked) => setVisibleColumns({ ...visibleColumns, type: checked })}
                      >
                        {t('reports.type')}
                      </DropdownMenuCheckboxItem>
                      <DropdownMenuCheckboxItem
                        checked={visibleColumns.number}
                        onCheckedChange={(checked) => setVisibleColumns({ ...visibleColumns, number: checked })}
                      >
                        {t('reports.number')}
                      </DropdownMenuCheckboxItem>
                      <DropdownMenuCheckboxItem
                        checked={visibleColumns.reference}
                        onCheckedChange={(checked) => setVisibleColumns({ ...visibleColumns, reference: checked })}
                      >
                        {t('reports.reference')}
                      </DropdownMenuCheckboxItem>
                      <DropdownMenuCheckboxItem
                        checked={visibleColumns.note}
                        onCheckedChange={(checked) => setVisibleColumns({ ...visibleColumns, note: checked })}
                      >
                        {t('reports.note')}
                      </DropdownMenuCheckboxItem>
                      <DropdownMenuCheckboxItem
                        checked={visibleColumns.taxableAmount}
                        onCheckedChange={(checked) => setVisibleColumns({ ...visibleColumns, taxableAmount: checked })}
                      >
                        {t('reports.taxableAmount')}
                      </DropdownMenuCheckboxItem>
                      <DropdownMenuCheckboxItem
                        checked={visibleColumns.vatDebit}
                        onCheckedChange={(checked) => setVisibleColumns({ ...visibleColumns, vatDebit: checked })}
                      >
                        {t('reports.vatDebit')}
                      </DropdownMenuCheckboxItem>
                      <DropdownMenuCheckboxItem
                        checked={visibleColumns.vatCredit}
                        onCheckedChange={(checked) => setVisibleColumns({ ...visibleColumns, vatCredit: checked })}
                      >
                        {t('reports.vatCredit')}
                      </DropdownMenuCheckboxItem>
                      <DropdownMenuCheckboxItem
                        checked={visibleColumns.balance}
                        onCheckedChange={(checked) => setVisibleColumns({ ...visibleColumns, balance: checked })}
                      >
                        {t('reports.balance')}
                      </DropdownMenuCheckboxItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button variant="outline" size="sm" onClick={handleCsv}>
                    <FileDown className="h-4 w-4 mr-2" />
                    CSV
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleCopy}>
                    <Copy className="h-4 w-4 mr-2" />
                    Copy
                  </Button>
                </div>
              </div>

              <div className="overflow-x-auto">
                <Table className="border-collapse">
                  <TableHeader>
                    <TableRow className="border-b-2 border-gray-300 bg-primary/5">
                      {visibleColumns.id && (
                        <TableHead className={`font-bold text-primary border border-gray-300 ${rtl ? 'text-right pr-3' : 'text-left pl-3'}`}>{t('reports.id')}</TableHead>
                      )}
                      {visibleColumns.date && (
                        <TableHead className={`font-bold text-primary border border-gray-300 ${rtl ? 'text-right pr-3' : 'text-left pl-3'}`}>{t('reports.date')}</TableHead>
                      )}
                      {visibleColumns.vendorCustomer && (
                        <TableHead className={`font-bold text-primary border border-gray-300 ${rtl ? 'text-right pr-3' : 'text-left pl-3'}`}>{t('reports.vendorCustomer')}</TableHead>
                      )}
                      {visibleColumns.type && (
                        <TableHead className={`font-bold text-primary border border-gray-300 ${rtl ? 'text-right pr-3' : 'text-left pl-3'}`}>{t('reports.type')}</TableHead>
                      )}
                      {visibleColumns.number && (
                        <TableHead className={`font-bold text-primary border border-gray-300 ${rtl ? 'text-right pr-3' : 'text-left pl-3'}`}>{t('reports.number')}</TableHead>
                      )}
                      {visibleColumns.reference && (
                        <TableHead className={`font-bold text-primary border border-gray-300 ${rtl ? 'text-right pr-3' : 'text-left pl-3'}`}>{t('reports.reference')}</TableHead>
                      )}
                      {visibleColumns.note && (
                        <TableHead className={`font-bold text-primary border border-gray-300 ${rtl ? 'text-right pr-3' : 'text-left pl-3'}`}>{t('reports.note')}</TableHead>
                      )}
                      {visibleColumns.taxableAmount && (
                        <TableHead className="font-bold text-primary text-right border border-gray-300 pr-3">{t('reports.taxableAmount')}</TableHead>
                      )}
                      {visibleColumns.vatDebit && (
                        <TableHead className="font-bold text-primary text-right border border-gray-300 pr-3">{t('reports.vatDebit')}</TableHead>
                      )}
                      {visibleColumns.vatCredit && (
                        <TableHead className="font-bold text-primary text-right border border-gray-300 pr-3">{t('reports.vatCredit')}</TableHead>
                      )}
                      {visibleColumns.balance && (
                        <TableHead className={`font-bold text-primary text-right border border-gray-300 pr-3`}>{t('reports.balance')}</TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredData.map((entry, index) => (
                      <TableRow
                        key={index}
                        className="border-b border-gray-300 hover:bg-primary/5 transition-colors"
                      >
                        {visibleColumns.id && (
                          <TableCell className={`px-3 py-3 border border-gray-300 ${rtl ? 'text-right' : 'text-left'}`}>{entry.id}</TableCell>
                        )}
                        {visibleColumns.date && (
                          <TableCell className={`px-3 py-3 border border-gray-300 ${rtl ? 'text-right' : 'text-left'}`}>{formatDate(entry.date)}</TableCell>
                        )}
                        {visibleColumns.vendorCustomer && (
                          <TableCell className={`px-3 py-3 border border-gray-300 ${rtl ? 'text-right' : 'text-left'}`}>{entry.vendorCustomer || '-'}</TableCell>
                        )}
                        {visibleColumns.type && (
                          <TableCell className={`px-3 py-3 border border-gray-300 ${rtl ? 'text-right' : 'text-left'}`}>{entry.type || '-'}</TableCell>
                        )}
                        {visibleColumns.number && (
                          <TableCell className={`px-3 py-3 border border-gray-300 ${rtl ? 'text-right' : 'text-left'}`}>{entry.number || '-'}</TableCell>
                        )}
                        {visibleColumns.reference && (
                          <TableCell className={`px-3 py-3 border border-gray-300 ${rtl ? 'text-right' : 'text-left'}`}>{entry.reference || '-'}</TableCell>
                        )}
                        {visibleColumns.note && (
                          <TableCell className={`px-3 py-3 border border-gray-300 ${rtl ? 'text-right' : 'text-left'}`}>{entry.note || '-'}</TableCell>
                        )}
                        {visibleColumns.taxableAmount && (
                          <TableCell className="text-right px-3 py-3 border border-gray-300">{formatAmount(entry.taxableAmount)}</TableCell>
                        )}
                        {visibleColumns.vatDebit && (
                          <TableCell className="text-right px-3 py-3 border border-gray-300">{formatAmount(entry.vatDebit)}</TableCell>
                        )}
                        {visibleColumns.vatCredit && (
                          <TableCell className="text-right px-3 py-3 border border-gray-300">{formatAmount(entry.vatCredit)}</TableCell>
                        )}
                        {visibleColumns.balance && (
                          <TableCell className="text-right px-3 py-3 border border-gray-300">{formatAmount(entry.balance)}</TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="border-t px-4 sm:px-6 py-4 flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="text-sm text-muted-foreground text-center sm:text-left">
                  Showing {(pageNumber - 1) * pageSize + 1} to{' '}
                  {Math.min(pageNumber * pageSize, totalCount)} of {totalCount} entries
                </div>
                <div className="flex items-center gap-3 flex-wrap justify-center">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground whitespace-nowrap">Page Size:</span>
                    <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                      <SelectTrigger className="w-[100px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageNumber((prev) => Math.max(prev - 1, 1))}
                    disabled={pageNumber === 1 || loading}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    <span className="hidden sm:inline ml-1">Previous</span>
                  </Button>
                  <div className="text-sm whitespace-nowrap px-2">
                    Page {pageNumber} of {totalPages || 1}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageNumber((prev) => Math.min(prev + 1, totalPages || 1))}
                    disabled={pageNumber >= totalPages || loading}
                  >
                    <span className="hidden sm:inline mr-1">Next</span>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

